import {
  AgentCategory,
  AgentDataDetailed,
  AgentDataReduced,
  AgentEndpointData,
  AgentProtocol,
  AgentService,
  AgentStatus,
  Massp4aiUseCase,
} from '../api/agentgarden.objects';
import { agentGardenApiRef } from '../api/agentgarden.api';
import {
  agentGardenImageResources,
  useAgentGardenWebResources,
} from '../resources/AgentGarden/AgentGardenResources';

import { Colors, useIsDarkMode } from './Colors';

import { useApi } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from './SnackbarContext';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Maps agent status to hex color code
 * @param status The agent status (e.g. 'production')
 * @returns The status hex color code
 */
export const useMapAgentStatusToColor = (status: AgentStatus) => {
  const isDarkMode = useIsDarkMode();
  const colors = isDarkMode
    ? Colors.Themes.Dark.AgentStatusChip
    : Colors.Themes.Light.AgentStatusChip;

  return status === 'production' ? colors.production : colors.development;
};

/**
 * Maps agent status to status display text
 * @param status The agent status (e.g. 'production')
 * @returns The status hex color code
 */
export const mapAgentStatusToText = (status: AgentStatus) => {
  return status === 'production' ? 'Live in Production' : 'Under Development';
};

/**
 * Maps a service or framework string to an image url, by using agent garden resources
 * @param agent The agent data to fetch the service or framework from
 * @param isDarkMode If light or dark mode mapping should happen
 * @returns The image url of the service
 */
export const mapAgentServiceToImage = (
  agent: AgentDataDetailed | AgentDataReduced | undefined,
  isDarkMode: boolean,
) => {
  const agentGardenImageUrls = agentGardenImageResources();

  if (!agent) {
    return agentGardenImageUrls.Default.url;
  }

  const service = getAgentFrameworkOrService(agent);

  if (!service) {
    return agentGardenImageUrls.Default.url;
  }

  if (isA2AAgent(service, agent.agentProtocol ?? '')) {
    return agentGardenImageUrls.A2AProtocol.url;
  }

  if (isMcpAgent(service, agent.agentProtocol ?? '')) {
    return isDarkMode
      ? agentGardenImageUrls.MCPProtocolDark.url
      : agentGardenImageUrls.MCPProtocol.url;
  }

  const serviceKey = (
    service !== '' ? service : 'Others'
  ) as keyof typeof agentGardenImageUrls;

  let imageUrl: string;

  if (isDarkMode && serviceKey.includes('AutoGen')) {
    imageUrl = agentGardenImageUrls.AutoGenLight.url;
  } else if (!isDarkMode && serviceKey.includes('AutoGen')) {
    imageUrl = agentGardenImageUrls.AutoGenDark.url;
  } else if (agentGardenImageUrls[serviceKey]) {
    imageUrl = agentGardenImageUrls[serviceKey].url;
  } else {
    imageUrl = agentGardenImageUrls.Default.url;
  }

  return imageUrl;
};

/**
 * Uses the backend-provided presigned logo URL when available,
 * and falls back to service/framework icon mapping otherwise.
 */
export const mapAgentToImage = (
  agent: AgentDataDetailed | AgentDataReduced | undefined,
  isDarkMode: boolean,
) => {
  const agentWithLegacyLogo = agent as
    | (AgentDataDetailed & { agent_logo_presigned_url?: string })
    | (AgentDataReduced & { agent_logo_presigned_url?: string })
    | undefined;

  const logoPresignedUrl =
    agentWithLegacyLogo?.customLogoPresignedUrl?.trim() ||
    agentWithLegacyLogo?.agent_logo_presigned_url?.trim() ||
    '';

  if (logoPresignedUrl) {
    return logoPresignedUrl;
  }

  return mapAgentServiceToImage(agent, isDarkMode);
};

/**
 * Maps agent data and endpoint data to the format required for editing an agent template
 * @param agentData The agent data to map
 * @param endpointData The endpoint data to map
 * @returns The formatted agent data for editing an agent template
 */
function getLogoPresignedUrl(agentData: AgentDataDetailed): string | undefined {
  const agentWithLegacyLogo = agentData as AgentDataDetailed & {
    agent_logo_presigned_url?: string;
  };
  return (
    agentWithLegacyLogo.customLogoPresignedUrl?.trim() ||
    agentWithLegacyLogo.agent_logo_presigned_url?.trim() ||
    undefined
  );
}

/**
 * Checks if the agent is a GCP, Microsoft Foundry, or A2A agent
 * @param service The service of the agent
 * @param protocol The protocol of the agent
 * @returns True if the agent is a GCP, Microsoft Foundry, or A2A agent, false otherwise
 */
function isGcpOrFoundryOrA2AAgent(service: string, protocol: string): boolean {
  return (
    isGcpAgent(service) ||
    isMicrosoftFoundryAgent(service) ||
    isA2AAgent(service, protocol)
  );
}

/**
 * Checks if the agent is an A2A or MCP agent
 * @param service The service of the agent
 * @param protocol The protocol of the agent
 * @returns True if the agent is an A2A or MCP agent, false otherwise
 */
function isA2AOrMcpAgent(service: string, protocol: string): boolean {
  return isA2AAgent(service, protocol) || isMcpAgent(service, protocol);
}

/**
 * Maps normal agent fields to the format required for editing an agent template
 * @param agentData The agent data to map
 * @returns The formatted agent data for editing an agent template
 */
function mapNormalFieldsToEditTemplate(agentData: AgentDataDetailed) {
  return {
    agentId: agentData.agentId,
    agentName: agentData.agentName,
    l3Manager: {
      userId: agentData.l3Manager.userId,
      email: agentData.l3Manager.email,
    },
    AIProductOwner: {
      userId: agentData.aiProductOwner.userId,
      email: agentData.aiProductOwner.email,
    },
    technicalContacts: agentData.technicalContacts.map(contact => ({
      userId: contact.userId,
      email: contact.email,
    })),
    informationOwner: {
      userId: agentData.informationOwner.userId,
      email: agentData.informationOwner.email,
    },
    status: mapAgentStatusToText(agentData.status),
    leanixAppId: agentData.leanixAppId ?? '',
    massp4aiUseCaseId: agentData.massp4aiUseCaseId ?? '',
    infoClassification: agentData.infoClassification ?? '',
    businessDomain: agentData.businessDomain ?? '',
    overview: agentData.overview ?? '',
    additionalDetails: agentData.additionalDetails ?? '',
    cloudProvider: agentData.cloudDeployments[0]?.cloudProvider ?? '',
    cloudRegion: agentData.cloudDeployments[0]?.cloudRegion ?? '',
    repoUrl: agentData.repoUrl ?? '',
    documentationUrl: agentData.documentationUrl ?? '',
    countries: {
      countrylist: agentData.countries,
    },
    acceptTerms: false,
  };
}

/**
 * Maps agent data and endpoint data to the format required for editing an agent template
 * @param agentData The agent data to map
 * @param endpointData The endpoint data to map
 * @returns The formatted agent data for editing an agent template
 */
export const mapAgentDataForEditTemplate = (
  agentData: AgentDataDetailed,
  endpointData: AgentEndpointData | null,
) => {
  const logoPresignedUrl = getLogoPresignedUrl(agentData);
  const protocol = agentData.agentProtocol ?? '';
  const isGcpOrFoundry = isGcpOrFoundryOrA2AAgent(
    agentData.agentService,
    protocol,
  );
  const isA2AOrMcp = isA2AOrMcpAgent(agentData.agentService, protocol);

  return {
    ...mapNormalFieldsToEditTemplate(agentData),

    ...(isUniversalAgentCategory(agentData.agentCategory) && {
      agentFramework: agentData.agentFramework ?? '',
      logoPresignedUrl: logoPresignedUrl,
      agentUrl: agentData.agentUrl ?? '',
      providerNpmMailbox: agentData.providerNpmMailbox ?? '',
    }),

    ...(isDatabricksAgent(agentData.agentService) && {
      taskAction: endpointData?.task ?? '',
    }),

    ...(isGcpAgent(agentData.agentService) && {
      gcpProjectId: endpointData?.gcpProjectId ?? '',
    }),

    ...(isCloudRunAgent(agentData.agentService) && {
      customAuthAudience: endpointData?.customAuthAudience ?? '',
    }),

    ...(isGcpOrFoundry && {
      supportedEndpointPaths: endpointData?.supportedEndpointPaths ?? [],
    }),

    ...(isEnterpriseAgentCategory(agentData.agentCategory) && {
      agentService: agentData.agentService,
      endpointUrl: endpointData?.agentServiceEndpointUrl,
      userRateLimit: endpointData?.userRateLimit ?? '',
      endpointRateLimit: endpointData?.endpointRateLimit ?? '',
    }),

    ...(isN8nAgent(agentData.agentService) && {
      baseUrl: endpointData?.baseUrl ?? '',
      requestFormat: endpointData?.requestFormat ?? '',
      responseFormat: endpointData?.responseFormat ?? '',
      memorySupport: {
        hasMemorySupport: endpointData?.hasMemorySupport ?? false,
        sessionIdField: endpointData?.sessionIdField ?? '',
      },
      n8nApiKey: {
        apiKeyExpiresAt: endpointData?.apiKeyExpiresAt ?? '',
      },
      headerAuthCredentials: {
        headerAuthKeyName: endpointData?.headerAuthKeyName ?? '',
      },
    }),

    ...(isA2AOrMcp && {
      agentProtocol: agentData?.agentProtocol ?? '',
      authType: endpointData?.authType
        ? {
            type: endpointData.authType,
            tokenUrl: endpointData?.tokenUrl ?? '',
            oauthScope: endpointData?.oauthScope ?? '',
            apiKeyLocation: endpointData?.apiKeyLocation ?? '',
            apiKeyLocationName: endpointData?.apiKeyLocationName ?? '',
            apiKeyExpiresAt: endpointData?.apiKeyExpiresAt ?? '',
          }
        : undefined,
    }),
  };
};

/**
 * Custom hook that provides a function to navigate to the edit agent template
 * @returns A function to navigate to edit agent template
 */
export function useNavigateToEditAgentTemplate() {
  const agentGardenApi = useApi(agentGardenApiRef);
  const agentGardenUrls = useAgentGardenWebResources();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  return async (agentId: string, agent?: AgentDataDetailed) => {
    try {
      const receivedAgentData =
        agent ?? (await agentGardenApi.getAgent(agentId));

      if (!receivedAgentData) {
        showSnackbar('error', "Error - Couldn't fetch agent data.");
        return;
      }

      const agentService = isEnterpriseAgentCategory(
        receivedAgentData.agentCategory,
      )
        ? receivedAgentData.agentService
        : receivedAgentData.agentFramework;

      const protocolFallback = receivedAgentData.agentProtocol ?? '';

      let endpointData = null;
      let agentProtocol = '';
      if (isEnterpriseAgentCategory(receivedAgentData.agentCategory)) {
        endpointData = await agentGardenApi.getGatewayEndpointDetails(
          agentId,
          receivedAgentData.agentService,
        );

        if (isA2AAgent(receivedAgentData.agentService, protocolFallback)) {
          agentProtocol = AgentProtocol.A2A;
        } else if (
          isMcpAgent(receivedAgentData.agentService, protocolFallback)
        ) {
          agentProtocol = AgentProtocol.MCP;
        }
      }

      const formattedAgentData = mapAgentDataForEditTemplate(
        receivedAgentData,
        endpointData,
      );

      const templateUrl = getTemplateUrlByAgentService(
        agentService ?? '',
        agentGardenUrls,
        agentProtocol,
      );

      // throw error if templateUrl is empty
      if (templateUrl === '') {
        throw new Error(
          `Error. Unsupported agent service: ${receivedAgentData.agentService}`,
        );
      }

      const href = `${templateUrl}?formData=${encodeURIComponent(JSON.stringify(formattedAgentData))}`;
      navigate(href);
    } catch (err) {
      showSnackbar(
        'error',
        `${err instanceof Error ? err.message : 'Error fetching agent data.'}`,
      );
    }
  };
}

/**
 * Returns the template URL for the given agent service
 * @param service The service of the agent
 * @param agentGardenUrls The agent garden URLs
 * @param protocol The protocol of the agent
 * @returns The template URL for the given agent service
 */
function getTemplateUrlByAgentService(
  service: string,
  agentGardenUrls: any,
  protocol: string,
): string {
  let templateUrl = '';

  switch (service) {
    case AgentService.Databricks:
      templateUrl = agentGardenUrls.DatabricksEditTemplate.urlWithoutBase;
      break;
    case AgentService.VertexAI:
    case AgentService.CloudRun:
      templateUrl =
        agentGardenUrls.GoogleCloudPlatformEditTemplate.urlWithoutBase;
      break;
    case AgentService.MicrosoftFoundry:
      templateUrl = agentGardenUrls.MicrosoftFoundryEditTemplate.urlWithoutBase;
      break;
    case AgentService.N8n:
      templateUrl = agentGardenUrls.n8nEditTemplate.urlWithoutBase;
      break;
    case AgentService.Langchain:
    case AgentService.LangchainWithLangraph:
    case AgentService.AgentFlow:
    case AgentService.AutoGen:
    case AgentService.CrewAI:
    case AgentService.LlamaIndex:
    case AgentService.MicrosoftSemanticKernel:
    case AgentService.Others:
      if (isA2AAgent(service, protocol)) {
        templateUrl = agentGardenUrls.A2AAgentEditTemplate.urlWithoutBase;
      } else if (isMcpAgent(service, protocol)) {
        templateUrl = agentGardenUrls.MCPAgentEditTemplate.urlWithoutBase;
      } else {
        templateUrl = agentGardenUrls.UniversalAgentEditTemplate.urlWithoutBase;
      }
      break;
    default:
      break;
  }

  return templateUrl;
}

/**
 * Checks if the given service is Databricks
 * @param service The service name to check
 * @returns True, if the service is Databricks, false otherwise
 */
export function isDatabricksAgent(service: string): boolean {
  return service === AgentService.Databricks;
}

/**
 * Checks if the given service is Vertex AI
 * @param service The service name to check
 * @returns True, if the service is Vertex AI, false otherwise
 */
export function isVertexAiAgent(service: string): boolean {
  return service === AgentService.VertexAI;
}

/**
 * Checks if the given service is Cloud Run
 * @param service The service name to check
 * @returns True, if the service is Cloud Run, false otherwise
 */
export function isCloudRunAgent(service: string): boolean {
  return service === AgentService.CloudRun;
}

/**
 * Checks if the given service is Microsoft Foundry
 * @param service The service name to check
 * @returns True, if the service is Microsoft Foundry, false otherwise
 */
export function isMicrosoftFoundryAgent(service: string): boolean {
  return service === AgentService.MicrosoftFoundry;
}

/**
 * Checks if the given service is GCP (Vertex AI or Cloud Run)
 * @param service The service name to check
 * @returns True, if the service is Vertex AI or Cloud Run, false otherwise
 */
export function isGcpAgent(service: string): boolean {
  return (AgentService.GCP as readonly string[]).includes(service);
}

/**
 * Checks if the given service is n8n Workflow Automation
 * @param service The service name to check
 * @returns True, if the service is n8n Workflow Automation, false otherwise
 */
export function isN8nAgent(service: string): boolean {
  return service === AgentService.N8n;
}

/**
 * Checks if the given service is A2A (Agent 2 Agent)
 * @param service The service name to check
 * @param protocol The protocol to check
 * @returns True, if the service is Agent 2 Agent, false otherwise
 */
export function isA2AAgent(service: string, protocol: string): boolean {
  return service === AgentService.Others && protocol === AgentProtocol.A2A;
}

/**
 * Checks if the given service is MCP (Model Context Protocol)
 * @param service The service name to check
 * @param protocol The protocol to check
 * @returns True, if the service is Model Context Protocol, false otherwise
 */
export function isMcpAgent(service: string, protocol: string): boolean {
  return service === AgentService.Others && protocol === AgentProtocol.MCP;
}

/**
 * Checks if the given category is Enterprise
 * @param category The category to check
 * @returns True, if the category is Enterprise, false otherwise
 */
export function isEnterpriseAgentCategory(category: AgentCategory): boolean {
  return category === AgentCategory.Enterprise;
}

/**
 * Checks if the given category is Universal
 * @param category The category to check
 * @returns True, if the category is Universal, false otherwise
 */

export function isUniversalAgentCategory(category: AgentCategory): boolean {
  return category === AgentCategory.Universal;
}

/**
 * Retrieves the framework or service of an agent, depending on its category
 * @param agent The agent to fetch the service or framework from
 * @returns The framework or service of the agent, or an empty string if not found
 */
export function getAgentFrameworkOrService(
  agent: AgentDataDetailed | AgentDataReduced,
): string | null {
  if (!agent) return null;

  return isEnterpriseAgentCategory(agent.agentCategory)
    ? agent.agentService
    : (agent.agentFramework ?? null);
}

/**
 * Sorts an array of agents alphabetically by agent name (ascending).
 */
export function sortAgents(
  unsortedAgents: AgentDataReduced[],
): AgentDataReduced[] {
  return unsortedAgents.toSorted((a, b) =>
    a.agentName.localeCompare(b.agentName),
  );
}

/**
 * Persists the dev-mode authorization flag to sessionStorage so the frontend can
 * conditionally show dev-only features. Clears the entry when the flag is absent
 * (e.g. in production environments) to prevent stale values.
 */
export function setDevModeAuthorization(
  isDevModeAuthorized: boolean | undefined,
): void {
  if (isDevModeAuthorized === undefined) {
    sessionStorage.removeItem('isDevModeAuthorized');
  } else {
    sessionStorage.setItem(
      'isDevModeAuthorized',
      isDevModeAuthorized.toString(),
    );
  }
}

/**
 * Maps the MASSP4AI use case information to a display format.
 * @param useCase - The MASSP4AI use case information.
 * @returns The display format of the MASSP4AI use case information.
 */
export function mapMassp4aiUseCaseInfoToDisplayData(
  useCase: Massp4aiUseCase | null,
): Record<string, { value: string; label: string }> {
  if (!useCase) {
    return {} as Record<string, { value: string; label: string }>;
  }
  return {
    name: { value: useCase.name ?? '', label: 'Use Case Name' },
    id: { value: useCase.id, label: 'Use Case ID' },
    shortDescription: {
      value: useCase.shortDescription ?? '',
      label: 'Use Case Description',
    },
    businessNeed: {
      value: useCase.businessNeed ?? '',
      label: 'Business Need',
    },
    expectedCompanyBenefit: {
      value: useCase.expectedCompanyBenefit ?? '',
      label: 'Expected Company Benefit',
    },
    expectedCustomerBenefit: {
      value: useCase.expectedCustomerBenefit ?? '',
      label: 'Expected Customer Benefit',
    },
    mitigationMeasuresScanStatus: {
      value: useCase.mitigationMeasuresScanStatus ?? '',
      label: 'Risk Assessment and Mitigation Measures',
    },
  };
}

/**
 * Renders the value cell for a use case info row. The risk assessment and
 * mitigation measures row is rendered as a status message with an icon,
 * while all other rows display the raw value.
 *
 * @param key - The use case info entry key
 * @param value - The use case info entry value
 * @returns The rendered cell content
 */
export function renderUseCaseInfoValue(key: string, value: string) {
  if (key !== 'mitigationMeasuresScanStatus') {
    return value && value.trim() !== '' ? value : '-';
  }

  return value === 'APPROVED' ? (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <CheckIcon color="success" />
      <Typography>
        MASSP4AI Risk Assessment and Mitigation Measures have been completed
        successfully. This agent is approved for onboarding.
      </Typography>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <ClearIcon color="error" />
      <Typography>
        MASSP4AI Risk Assessment and Mitigation Measures are not yet complete.
        This agent cannot be onboarded at this time.
      </Typography>
    </Box>
  );
}
